import { EditorState, Plugin, TextSelection, type Transaction } from "prosemirror-state";
import { EditorView } from "prosemirror-view";
import { keymap } from "prosemirror-keymap";
import { baseKeymap, toggleMark, setBlockType, chainCommands, exitCode } from "prosemirror-commands";
import { wrapInList, splitListItem, liftListItem, sinkListItem } from "prosemirror-schema-list";
import type { MarkType, Node as PmNode } from "prosemirror-model";
import type { RichTextDoc } from "@broadpaper/core";
import { richTextSchema, modelToPm, pmToModel } from "./schema.js";
import { autocompleteKeymap, tokenAutocomplete, type AutocompleteHandlers, type TokenTrigger } from "./autocomplete.js";

export interface RichTextViewOptions {
  element: HTMLElement;
  doc: RichTextDoc | undefined;
  labelFor: (expr: string) => string;
  onChange: (doc: RichTextDoc) => void;
  onSelectionChange?: (view: EditorView) => void;
  onEscape?: () => void;
  onBlur?: () => void;
  /** Single paragraph only (headings). */
  singleLine?: boolean;
  /** Fires when the user presses Enter in single-line mode. */
  onEnter?: () => void;
  /** Fires as a "{{" field trigger opens, moves or closes. */
  onTokenTrigger?: (trigger: TokenTrigger | null, view: EditorView) => void;
  /** Consulted before Enter/Tab/arrows, so an open suggestion list wins. */
  autocompleteHandlers?: () => AutocompleteHandlers | null;
}

export interface ActiveFormats {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strike: boolean;
  code: boolean;
  link: string | null;
  color: string | null;
  bulletList: boolean;
  orderedList: boolean;
  align: string | null;
}

export function createRichTextView(opts: RichTextViewOptions): { view: EditorView; destroy(): void; setDoc(doc: RichTextDoc | undefined): void } {
  const s = richTextSchema;
  const bindings: Record<string, (state: EditorState, dispatch?: (tr: Transaction) => void, view?: EditorView) => boolean> = {
    "Mod-b": toggleMark(s.marks.bold!),
    "Mod-i": toggleMark(s.marks.italic!),
    "Mod-u": toggleMark(s.marks.underline!),
    "Mod-Shift-x": toggleMark(s.marks.strike!),
    "Mod-e": toggleMark(s.marks.code!),
    "Shift-Enter": chainCommands(exitCode, (state, dispatch) => {
      dispatch?.(state.tr.replaceSelectionWith(s.nodes.hard_break!.create()).scrollIntoView());
      return true;
    }),
    Escape: () => {
      opts.onEscape?.();
      return true;
    }
  };
  if (opts.singleLine) {
    bindings["Enter"] = () => {
      opts.onEnter?.();
      return true;
    };
  } else {
    bindings["Enter"] = chainCommands(splitListItem(s.nodes.list_item!), baseKeymap["Enter"]!);
    bindings["Tab"] = sinkListItem(s.nodes.list_item!);
    bindings["Shift-Tab"] = liftListItem(s.nodes.list_item!);
    bindings["Mod-Shift-8"] = wrapInList(s.nodes.bullet_list!);
    bindings["Mod-Shift-7"] = wrapInList(s.nodes.ordered_list!);
  }

  const plugins = [
    ...(opts.autocompleteHandlers ? [keymap(autocompleteKeymap(opts.autocompleteHandlers))] : []),
    keymap(bindings),
    keymap(baseKeymap),
    ...(opts.onTokenTrigger ? [tokenAutocomplete({ onTrigger: opts.onTokenTrigger })] : []),
    new Plugin({
      props: {
        handleDOMEvents: {
          blur: () => {
            opts.onBlur?.();
            return false;
          }
        },
        // Never accept pasted HTML structure beyond what the schema allows; plain text is safest.
        clipboardTextParser: (text, $context) => {
          const paragraphs = text.split(/\r?\n/).map((line) => s.nodes.paragraph!.create(null, line ? [s.text(line)] : []));
          return s.nodes.doc!.create(null, paragraphs).slice(0, paragraphs.length ? undefined : 0) as never;
          void $context;
        }
      }
    })
  ];

  let suppress = false;
  const state = EditorState.create({ doc: modelToPm(opts.doc, opts.labelFor), plugins });
  const view = new EditorView(opts.element, {
    state,
    attributes: { class: "bpe-rte", spellcheck: "true" },
    dispatchTransaction(tr) {
      const next = view.state.apply(tr);
      view.updateState(next);
      if (tr.docChanged && !suppress) opts.onChange(pmToModel(next.doc));
      if (tr.selectionSet || tr.docChanged) opts.onSelectionChange?.(view);
    }
  });

  return {
    view,
    destroy: () => view.destroy(),
    setDoc(doc) {
      const pm = modelToPm(doc, opts.labelFor);
      if (pm.eq(view.state.doc)) return;
      suppress = true;
      const tr = view.state.tr.replaceWith(0, view.state.doc.content.size, pm.content);
      view.dispatch(tr);
      suppress = false;
    }
  };
}

export function activeFormats(view: EditorView): ActiveFormats {
  const { state } = view;
  const { from, $from, to, empty } = state.selection;
  const has = (type: MarkType): boolean => {
    if (empty) return !!type.isInSet(state.storedMarks ?? $from.marks());
    return state.doc.rangeHasMark(from, to, type);
  };
  const markAttr = (type: MarkType, attr: string): string | null => {
    const marks = empty ? state.storedMarks ?? $from.marks() : $from.marks();
    const m = marks.find((x) => x.type === type);
    return m ? String(m.attrs[attr]) : null;
  };
  let bulletList = false;
  let orderedList = false;
  for (let d = $from.depth; d > 0; d--) {
    const n = $from.node(d);
    if (n.type.name === "bullet_list") bulletList = true;
    if (n.type.name === "ordered_list") orderedList = true;
  }
  const para = $from.parent.type.name === "paragraph" ? $from.parent : null;
  return {
    bold: has(richTextSchema.marks.bold!),
    italic: has(richTextSchema.marks.italic!),
    underline: has(richTextSchema.marks.underline!),
    strike: has(richTextSchema.marks.strike!),
    code: has(richTextSchema.marks.code!),
    link: markAttr(richTextSchema.marks.link!, "href"),
    color: markAttr(richTextSchema.marks.color!, "color"),
    bulletList,
    orderedList,
    align: para ? (para.attrs.align as string | null) : null
  };
}

export const rteCommands = {
  toggle(view: EditorView, mark: "bold" | "italic" | "underline" | "strike" | "code") {
    toggleMark(richTextSchema.marks[mark]!)(view.state, view.dispatch);
    view.focus();
  },
  setLink(view: EditorView, href: string | null) {
    const type = richTextSchema.marks.link!;
    const { from, to, empty } = view.state.selection;
    let tr = view.state.tr;
    if (empty) {
      if (href) tr = tr.addStoredMark(type.create({ href }));
      else tr = tr.removeStoredMark(type);
    } else {
      tr = tr.removeMark(from, to, type);
      if (href) tr = tr.addMark(from, to, type.create({ href }));
    }
    view.dispatch(tr);
    view.focus();
  },
  setColor(view: EditorView, color: string | null) {
    const type = richTextSchema.marks.color!;
    const { from, to, empty } = view.state.selection;
    let tr = view.state.tr;
    if (empty) tr = color ? tr.addStoredMark(type.create({ color })) : tr.removeStoredMark(type);
    else {
      tr = tr.removeMark(from, to, type);
      if (color) tr = tr.addMark(from, to, type.create({ color }));
    }
    view.dispatch(tr);
    view.focus();
  },
  setAlign(view: EditorView, align: string | null) {
    const { from, to } = view.state.selection;
    let tr = view.state.tr;
    view.state.doc.nodesBetween(from, to, (node, pos) => {
      if (node.type.name === "paragraph") tr = tr.setNodeMarkup(pos, undefined, { ...node.attrs, align });
    });
    view.dispatch(tr);
    view.focus();
  },
  toggleList(view: EditorView, kind: "bullet" | "ordered") {
    const formats = activeFormats(view);
    const active = kind === "bullet" ? formats.bulletList : formats.orderedList;
    if (active) liftListItem(richTextSchema.nodes.list_item!)(view.state, view.dispatch);
    else {
      const ok = wrapInList(richTextSchema.nodes[kind === "bullet" ? "bullet_list" : "ordered_list"]!)(view.state, view.dispatch);
      if (!ok) {
        // Switch list type
        liftListItem(richTextSchema.nodes.list_item!)(view.state, view.dispatch);
        wrapInList(richTextSchema.nodes[kind === "bullet" ? "bullet_list" : "ordered_list"]!)(view.state, view.dispatch);
      }
    }
    view.focus();
  },
  insertToken(view: EditorView, expr: string, label: string) {
    const node = richTextSchema.nodes.token!.create({ expr, label });
    const tr = view.state.tr.replaceSelectionWith(node).scrollIntoView();
    view.dispatch(tr);
    view.focus();
  },
  insertText(view: EditorView, text: string) {
    view.dispatch(view.state.tr.insertText(text).scrollIntoView());
    view.focus();
  },
  paragraph(view: EditorView) {
    setBlockType(richTextSchema.nodes.paragraph!)(view.state, view.dispatch);
  },
  selectAll(view: EditorView) {
    view.dispatch(view.state.tr.setSelection(TextSelection.create(view.state.doc, 0, view.state.doc.content.size)));
  }
};

export type { PmNode, EditorView };
